import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Checkbox, DatePicker, InputNumber, Modal, Segmented, Select, Spin, Switch, Table, message } from "antd";
import { CheckCircle2, RefreshCw, Send, Smartphone, XCircle } from "lucide-react";
import dayjs from "dayjs";
import { AN_COLORS, AN_FONT } from "../../design-system/tokens";
import { StatusPill, anTableProps, fmtDate, tableEmpty } from "../../design-system/tableUi";
import { Note } from "../../pages/enduser/ssh-proxy/sshProxyUi";
import { SettingsField, SettingsSection } from "./settingsUi";
import { isAdminUser } from "../../utils/helpers";
import { ORG_ID } from "../../constants";
import { logOutUser } from "../../common";
import {
  dispatchAdminActivityList,
  dispatchAdminDevicesApprove,
  dispatchAdminDevicesPending,
  dispatchCreateEnrollmentCampaign,
  dispatchGetDeviceSettings,
  dispatchGetEnrollmentCampaign,
  dispatchGetOnboardingSettings,
  dispatchSetDeviceSettings,
  dispatchSetOnboardingSettings,
} from "../../utils/apiservice";
import { providerName } from "../enrollment/enrollmentCommon";

// Settings → MFA Provider, when the AuthNull Authenticator is selected: the app, org-wide.
//
// Backend: internal/mfa/routes.go (admin routes), internal/mfa/handler/
// admin_devices_handler.go, onboarding_settings_handler.go, internal/mfacampaign.
// Every call is ADMIN-only and takes the org from the session — none sends an
// orgId. A user's own phones are managed from their row in Users & Access.

// Promise wrapper so the page can await the callback-style dispatchers.
const call = (dispatch, payload) =>
  new Promise((resolve, reject) => {
    dispatch({
      payload,
      onSuccess: resolve,
      onFailure: (e) => {
        if (e?.isLogout) logOutUser();
        reject(e);
      },
      onForbidden: reject,
      onNotFound: reject,
      onError: reject,
    });
  });

const errText = (e, fallback) => e?.message || fallback;
const muted = (s = "—") => <span style={{ color: AN_COLORS.muted }}>{s}</span>;

// ── New users ────────────────────────────────────────────────────────────────

function OnboardingSection() {
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    call(dispatchGetOnboardingSettings)
      .then(setSettings)
      .catch((e) => setError(errText(e, "Could not load the onboarding setting.")));
  }, []);

  const toggle = async (sendInvite) => {
    setSaving(true);
    setError("");
    try {
      const res = await call(dispatchSetOnboardingSettings, { sendInvite });
      setSettings((s) => ({ ...s, ...res, sendInvite }));
    } catch (e) {
      setError(errText(e, "Could not save the onboarding setting."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsSection
      title="New users"
      description="When someone is added to this tenant — from the console, an import or tenant sign-up — they can get an email with a QR code, a link and an activation code for the Authenticator app."
    >
      {error && <Note tone="danger" icon={<XCircle size={15} />}>{error}</Note>}
      <SettingsField
        last
        label="Email an Authenticator invite to new users"
        help={
          settings && settings.inviteApplies === false
            ? `Push MFA for this tenant is handled by ${providerName(settings.provider)}, so no Authenticator invite is sent while that is the provider.`
            : "Sent alongside the set-password email. The invite is valid for 7 days and can be re-issued from the user's row."
        }
      >
        {settings ? (
          <Switch checked={!!settings.sendInvite} loading={saving} onChange={toggle} />
        ) : error ? null : (
          <Spin size="small" />
        )}
      </SettingsField>
    </SettingsSection>
  );
}

// ── Adding phones (per-org device rules) ────────────────────────────────────

const NEW_DEVICE_CHECKS = [
  { value: "email_otp", label: "Approve on another phone, or email code" },
  { value: "sibling", label: "Approve on another phone" },
  { value: "admin", label: "An admin approves each new phone" },
];
const NUMBER_MATCHING = [
  { value: "off", label: "Off" },
  { value: "high_risk", label: "Only for risky sign-ins" },
  { value: "always", label: "Always" },
];
const SETTING_KEYS = ["maxDevicesPerUser", "allowAppPasswordLogin", "newDeviceCheck", "browserSigninSkipsCheck", "numberMatching"];

function DeviceSettingsSection({ notify }) {
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    call(dispatchGetDeviceSettings)
      .then((res) => { setSaved(res); setDraft(res); })
      .catch((e) => setError(errText(e, "Could not load these settings.")));
  }, []);

  const set = (key) => (value) => setDraft((d) => ({ ...d, [key]: value }));
  // Only what changed is sent: a field the server does not know yet is never
  // posted unless the admin actually touched it.
  const changes = draft && saved
    ? Object.fromEntries(SETTING_KEYS.filter((k) => draft[k] !== saved[k] && draft[k] !== undefined).map((k) => [k, draft[k]]))
    : {};
  const dirty = Object.keys(changes).length > 0;

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const res = await call(dispatchSetDeviceSettings, changes);
      setSaved(res);
      setDraft(res);
      notify.success("Saved.");
    } catch (e) {
      setError(errText(e, "Could not save these settings."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsSection
      title="Adding phones"
      description="How many phones each user can have, and what has to happen before a new phone can approve sign-ins."
      actions={draft && (
        <span style={{ display: "inline-flex", gap: 8 }}>
          {dirty && <Button size="small" onClick={() => setDraft(saved)} disabled={saving}>Discard</Button>}
          <Button size="small" type="primary" onClick={save} loading={saving} disabled={!dirty}>Save</Button>
        </span>
      )}
    >
      {error && <Note tone="danger" icon={<XCircle size={15} />}>{error}</Note>}
      {!draft ? (error ? null : <Spin size="small" />) : (
        <>
          <SettingsField label="Phones per user" help="1 to 10. A user at the limit signs an old phone out before adding a new one.">
            <InputNumber min={1} max={10} precision={0} value={draft.maxDevicesPerUser} onChange={set("maxDevicesPerUser")} style={{ width: 100 }} />
          </SettingsField>
          <SettingsField label="Check before a new phone is added">
            <Select value={draft.newDeviceCheck} onChange={set("newDeviceCheck")} options={NEW_DEVICE_CHECKS} style={{ width: 320, maxWidth: "100%" }} />
          </SettingsField>
          <SettingsField label="Users can add a phone by signing in to the app with their password">
            <Switch checked={!!draft.allowAppPasswordLogin} onChange={set("allowAppPasswordLogin")} />
          </SettingsField>
          <SettingsField
            label="Adding a phone through the browser sign-in skips the extra check"
            help={draft.newDeviceCheck === "admin"
              ? "Has no effect while an admin approves each new phone."
              : "The browser sign-in already runs your sign-in page and its MFA."}
          >
            <Switch checked={!!draft.browserSigninSkipsCheck} onChange={set("browserSigninSkipsCheck")} disabled={draft.newDeviceCheck === "admin"} />
          </SettingsField>
          <SettingsField
            last
            label="Number matching"
            help="Console sign-ins show a number that the user picks on their phone. Only for risky sign-ins needs risk scoring switched on."
          >
            <Select value={draft.numberMatching || "off"} onChange={set("numberMatching")} options={NUMBER_MATCHING} style={{ width: 240 }} />
          </SettingsField>
        </>
      )}
    </SettingsSection>
  );
}

// ── Phones waiting for approval ─────────────────────────────────────────────

function PendingSection({ notify }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [deciding, setDeciding] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await call(dispatchAdminDevicesPending);
      setRows(res?.pending || []);
      setError("");
    } catch (e) {
      setError(errText(e, "Could not load pending phones."));
    }
  }, []);

  // Someone is usually waiting on the other end of these: keep the list fresh.
  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const decide = async (row, approve) => {
    setDeciding(row.loginTicket);
    try {
      await call(dispatchAdminDevicesApprove, { loginTicket: row.loginTicket, approve });
      notify.success(approve ? `${row.deviceName || "The phone"} can now finish signing in.` : "Refused.");
      await load();
    } catch (e) {
      notify.error(errText(e, "Could not record the decision."));
    } finally {
      setDeciding(null);
    }
  };

  const columns = [
    { title: "User", dataIndex: "email", render: (v) => v || muted() },
    {
      title: "Phone",
      dataIndex: "deviceName",
      render: (v, r) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Smartphone size={14} color={AN_COLORS.muted} />
          {v || "Unnamed device"}
          {r.platform && <span style={{ color: AN_COLORS.muted }}>({r.platform})</span>}
        </span>
      ),
    },
    { title: "Requested", dataIndex: "requestedAt", render: fmtDate },
    {
      title: "",
      key: "actions",
      align: "right",
      render: (_, r) => (
        <span style={{ display: "inline-flex", gap: 8 }}>
          <Button size="small" onClick={() => decide(r, false)} disabled={!!deciding}>Refuse</Button>
          <Button size="small" type="primary" loading={deciding === r.loginTicket} disabled={!!deciding} onClick={() => decide(r, true)}>
            Approve
          </Button>
        </span>
      ),
    },
  ];

  // Only orgs that require an administrator to approve new phones ever have
  // anything here, and the setting itself is not exposed yet — so the section
  // shows only while someone is actually waiting (or the list cannot load).
  if (!error && (!rows || rows.length === 0)) return null;

  return (
    <SettingsSection
      title="Phones waiting for approval"
      description="When the tenant requires an administrator to approve new phones, a user who signs in from the app waits here. Approve only phones you expect: an approved phone can approve that user's sign-ins."
      actions={<Button size="small" icon={<RefreshCw size={13} style={{ verticalAlign: -2 }} />} onClick={load}>Refresh</Button>}
    >
      {error ? (
        <Note tone="danger" icon={<XCircle size={15} />}>{error}</Note>
      ) : (
        <Table
          {...anTableProps}
          rowKey="loginTicket"
          columns={columns}
          dataSource={rows || []}
          loading={rows === null}
          pagination={false}
          locale={tableEmpty(rows === null, "Nobody is waiting.")}
        />
      )}
    </SettingsSection>
  );
}

// ── Invite campaign ─────────────────────────────────────────────────────────

const REMINDER_OPTIONS = [
  { label: "7 days before", value: 7 },
  { label: "3 days before", value: 3 },
  { label: "1 day before", value: 1 },
];
const campaignKey = () => `authenticatorCampaign:${ORG_ID()}`;

function CampaignSection({ notify }) {
  const [identity, setIdentity] = useState("ad");
  const [deadline, setDeadline] = useState(null);
  const [reminders, setReminders] = useState([7, 1]);
  const [confirm, setConfirm] = useState(false);
  const [starting, setStarting] = useState(false);
  const [campaign, setCampaign] = useState(null);
  const [error, setError] = useState("");
  const timer = useRef(null);

  const refresh = useCallback(async (id) => {
    if (!id) return;
    try {
      const c = await call(dispatchGetEnrollmentCampaign, { campaignId: id });
      // A reply with no campaign in it (deleted, or another org's id) is not one.
      if (!c?.id) {
        try { localStorage.removeItem(campaignKey()); } catch (_) { /* ignore */ }
        setCampaign(null);
        clearInterval(timer.current);
        return;
      }
      setCampaign({ ...c });
      if (c?.status && !["running", "pending", "queued"].includes(c.status)) {
        clearInterval(timer.current);
      }
    } catch (e) {
      if (e?.status === 404) {
        try { localStorage.removeItem(campaignKey()); } catch (_) { /* ignore */ }
        setCampaign(null);
      }
      clearInterval(timer.current);
    }
  }, []);

  const watch = useCallback((id) => {
    clearInterval(timer.current);
    refresh(id);
    timer.current = setInterval(() => refresh(id), 5000);
  }, [refresh]);

  useEffect(() => {
    let last = null;
    try { last = Number(localStorage.getItem(campaignKey())) || null; } catch (_) { /* ignore */ }
    if (last) watch(last);
    return () => clearInterval(timer.current);
  }, [watch]);

  const start = async () => {
    setStarting(true);
    setError("");
    try {
      const res = await call(dispatchCreateEnrollmentCampaign, {
        identity,
        scope: "all",
        ...(deadline ? { deadline: deadline.endOf("day").toISOString(), reminderDays: reminders } : {}),
      });
      try { localStorage.setItem(campaignKey(), String(res.campaignId)); } catch (_) { /* ignore */ }
      notify.success(`Sending ${res.toInvite} invite${res.toInvite === 1 ? "" : "s"} — ${res.skipped} already enrolled.`);
      setConfirm(false);
      watch(res.campaignId);
    } catch (e) {
      setError(errText(e, "Could not start the campaign."));
    } finally {
      setStarting(false);
    }
  };

  const who = identity === "ad" ? "directory (AD / Entra) users" : "console users";

  return (
    <SettingsSection
      title="Invite everyone who isn't set up yet"
      description="Emails an Authenticator invite to every user without a phone. Users who already have a phone are skipped, and users without an email address are counted as failed."
    >
      <SettingsField label="Who">
        <Segmented
          value={identity}
          onChange={setIdentity}
          options={[
            { label: "Directory users", value: "ad" },
            { label: "Console users", value: "platform" },
          ]}
        />
      </SettingsField>
      <SettingsField label="Deadline" hint="optional" help="Reminder emails go out before the deadline to anyone still not set up.">
        <div style={{ display: "grid", gap: 10 }}>
          <DatePicker
            value={deadline}
            onChange={setDeadline}
            disabledDate={(d) => d && d.isBefore(dayjs().endOf("day"))}
            style={{ width: 220 }}
          />
          {deadline && (
            <Checkbox.Group options={REMINDER_OPTIONS} value={reminders} onChange={setReminders} />
          )}
        </div>
      </SettingsField>
      <SettingsField last label="">
        <Button type="primary" icon={<Send size={13} style={{ verticalAlign: -2 }} />} onClick={() => setConfirm(true)}>
          Send invites
        </Button>
      </SettingsField>

      {campaign && <CampaignProgress campaign={campaign} />}

      <Modal
        open={confirm}
        title="Send Authenticator invites?"
        onCancel={starting ? undefined : () => setConfirm(false)}
        footer={[
          <Button key="cancel" onClick={() => setConfirm(false)} disabled={starting}>Cancel</Button>,
          <Button key="go" type="primary" loading={starting} onClick={start}>Send</Button>,
        ]}
      >
        <div style={{ display: "grid", gap: 12, fontFamily: AN_FONT, fontSize: 13, color: AN_COLORS.ink2 }}>
          <div>
            Every one of your {who} without a phone gets an email now
            {deadline ? `, with reminders before ${deadline.format("D MMM YYYY")}` : ""}.
            An earlier unused invite for the same user stops working.
          </div>
          {error && <Note tone="danger" icon={<XCircle size={15} />}>{error}</Note>}
        </div>
      </Modal>
    </SettingsSection>
  );
}

function CampaignProgress({ campaign }) {
  const toInvite = Math.max(0, (campaign.targets || 0) - (campaign.skipped || 0));
  const done = (campaign.invited || 0) + (campaign.failed || 0);
  const stat = (label, value, color) => (
    <div style={{ minWidth: 96 }}>
      <div style={{ fontSize: 20, fontWeight: 600, color: color || AN_COLORS.ink }}>{value ?? 0}</div>
      <div style={{ fontSize: 12, color: AN_COLORS.muted }}>{label}</div>
    </div>
  );
  return (
    <div style={{
      marginTop: 14, padding: 16, border: `1px solid ${AN_COLORS.line}`, borderRadius: 10,
      background: AN_COLORS.surface, fontFamily: AN_FONT,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: AN_COLORS.ink }}>Last campaign</span>
        <StatusPill status={campaign.status === "done" || campaign.status === "finished" ? "success" : campaign.status || "unknown"} label={campaign.status} />
        <span style={{ fontSize: 12, color: AN_COLORS.muted }}>
          started {fmtDate(campaign.createdAt)}
          {campaign.deadline ? ` · deadline ${fmtDate(campaign.deadline)}` : ""}
        </span>
      </div>
      <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
        {stat("users", campaign.targets)}
        {stat("already set up", campaign.skipped, AN_COLORS.success)}
        {stat("invited", campaign.invited)}
        {stat("failed", campaign.failed, campaign.failed ? AN_COLORS.danger : undefined)}
        {stat("left to send", Math.max(0, toInvite - done))}
      </div>
    </div>
  );
}

// ── Recent approvals ────────────────────────────────────────────────────────

// Filter dropdowns above the table: a small label over each control.
const FILTER_LABEL = {
  display: "grid", gap: 6, fontFamily: AN_FONT, fontSize: 12, fontWeight: 500, color: AN_COLORS.muted,
};

const STATUS_FILTERS = [
  { label: "All results", value: "" },
  { label: "Denied", value: "denied" },
  { label: "Expired", value: "expired" },
  { label: "Approved", value: "approved" },
];

/**
 * Push history for the whole org, whichever provider sent the push: the
 * AuthNull Authenticator, Okta or Duo. Shown on the MFA Provider screen for
 * every provider, not only the Authenticator.
 */
// Which provider sent a push. The device providers (fcm, expo, empty on
// older rows) are all the AuthNull Authenticator.
const ASSET = (file) => `${process.env.PUBLIC_URL}/${file}`;
const PUSH_PROVIDERS = {
  authnull: { name: "AuthNull Authenticator", img: ASSET("logo_blue.png") },
  okta: { name: "Okta Verify", img: ASSET("okta-app.png") },
  duo: { name: "Duo", img: ASSET("duo.png") },
  azure_ad: { name: "Microsoft Entra ID", img: null },
};
const providerOf = (raw) => {
  const p = String(raw ?? "").toLowerCase();
  return !p || p === "fcm" || p === "expo" ? "authnull" : p;
};

// Logo + name. `compact` sizes it to sit inside a dropdown's single line, so
// the Provider and Result dropdowns stay the same height.
function ProviderCell({ provider, compact = false }) {
  if (provider === undefined) return muted();
  const id = providerOf(provider);
  const meta = PUSH_PROVIDERS[id] || { name: id, img: null };
  const size = compact ? 14 : 18;
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center", gap: compact ? 6 : 7, verticalAlign: "middle",
        // One line inside the dropdown; in the table a long name may wrap, beside its logo.
        ...(compact ? { whiteSpace: "nowrap", lineHeight: 1 } : { lineHeight: 1.35 }),
      }}
      title={meta.name}
    >
      {meta.img
        ? <img src={meta.img} alt="" width={size} height={size} style={{ borderRadius: 3, objectFit: "contain", display: "block", flexShrink: 0 }} />
        : <span style={{ width: size, height: size, borderRadius: 3, background: AN_COLORS.bg, border: `1px solid ${AN_COLORS.line}` }} />}
      <span style={{ color: AN_COLORS.ink2 }}>{meta.name}</span>
    </span>
  );
}

// Every provider a push can come from, in the Provider filter.
const PROVIDER_FILTERS = ["authnull", "okta", "duo"];
const providersParam = (id) => (id === "authnull" ? ["authnull", "fcm", "expo", ""] : [id]);

export function PushHistorySection({ provider: selected }) {
  const selectedId = selected ? providerOf(selected) : null;
  // The selected provider's pushes by default: an org that just switched to
  // the Authenticator wants to see the Authenticator's pushes, not Okta's.
  // "all" or one provider id; starts on the provider in force.
  const [scope, setScope] = useState(selectedId || "all");
  const [status, setStatus] = useState("");
  const [rows, setRows] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async (before, append) => {
    try {
      const onlySelected = scope !== "all" ? scope : null;
      const res = await call(dispatchAdminActivityList, {
        limit: 50,
        ...(status ? { statuses: [status] } : {}),
        ...(before ? { before } : {}),
        // Filtered on the server when it supports it…
        ...(onlySelected ? { providers: providersParam(onlySelected) } : {}),
      });
      // …and here too, for a server that returns `provider` but does not filter
      // yet. Rows without the field (an older server) cannot be told apart.
      const items = (res?.items || []).filter(
        (r) => !onlySelected || r.provider === undefined || providerOf(r.provider) === onlySelected
      );
      setRows((prev) => (append ? [...(prev || []), ...items] : items));
      setCursor(res?.nextCursor || null);
      setError("");
    } catch (e) {
      setError(errText(e, "Could not load activity."));
      if (!append) setRows([]);
    }
  }, [status, scope]);

  useEffect(() => { setRows(null); load(null, false); }, [load]);

  const more = async () => {
    setLoadingMore(true);
    await load(cursor, true);
    setLoadingMore(false);
  };

  const columns = [
    { title: "When", dataIndex: "createdAt", render: fmtDate, width: 190 },
    { title: "Provider", dataIndex: "provider", width: 210, render: (p) => <ProviderCell provider={p} /> },
    { title: "User", dataIndex: "email", render: (v) => v || muted() },
    {
      title: "Sign-in to",
      key: "resource",
      render: (_, r) => {
        const name = r.resource?.name;
        const type = r.resource?.type;
        return name || type ? (
          <span>{name || type}{name && type && <span style={{ color: AN_COLORS.muted }}> · {type}</span>}</span>
        ) : muted(r.flow === "platform" ? "Console" : "—");
      },
    },
    { title: "From", key: "from", render: (_, r) => r.location?.display || r.clientIp || r.location?.ip || muted() },
    {
      title: "Result",
      dataIndex: "status",
      render: (s, r) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <StatusPill status={s === "denied" ? "failed" : s === "expired" ? "inactive" : s} label={s} />
          {r.resolutionSource === "ttl" && <span style={{ color: AN_COLORS.muted, fontSize: 12 }}>no answer</span>}
        </span>
      ),
    },
    {
      title: "Risk",
      key: "risk",
      render: (_, r) => {
        const level = String(r.risk?.level || "").toLowerCase();
        if (!level) return muted();
        return <StatusPill status={level === "high" ? "failed" : level === "medium" ? "warning" : "success"} label={level} />;
      },
    },
  ];

  return (
    <SettingsSection
      title="Recent push approvals"
      description="Push sign-in requests in this tenant, newest first: the selected provider's by default, or every provider's. A denial worth investigating looks different from a request nobody answered."
    >
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-end", padding: "16px 16px 14px" }}>
        <label style={FILTER_LABEL}>
          Provider
          <Select
            value={scope}
            onChange={setScope}
            style={{ width: 240 }}
            options={[
              { value: "all", label: "All providers" },
              ...PROVIDER_FILTERS.map((id) => ({ value: id, label: <ProviderCell provider={id} compact /> })),
            ]}
          />
        </label>
        <label style={FILTER_LABEL}>
          Result
          <Select value={status} onChange={setStatus} style={{ width: 160 }} options={STATUS_FILTERS} />
        </label>
      </div>
      {error ? (
        <Note tone="danger" icon={<XCircle size={15} />}>{error}</Note>
      ) : (
        <>
          <Table
            {...anTableProps}
            rowKey={(r) => r.id ?? `${r.flow}:${r.challengeId}`}
            columns={columns}
            dataSource={rows || []}
            loading={rows === null}
            pagination={false}
            locale={tableEmpty(rows === null, "No push approvals yet.")}
          />
          {cursor && (
            <div style={{ textAlign: "center", marginTop: 12 }}>
              <Button onClick={more} loading={loadingMore}>Load more</Button>
            </div>
          )}
        </>
      )}
    </SettingsSection>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

/**
 * The AuthNull Authenticator's org-wide settings, shown inside Settings › MFA
 * Provider when the Authenticator is the provider: invites for new users, the
 * rules for adding phones, phones waiting for approval and bulk invites. The push
 * history (PushHistorySection) is shown for every provider. A user's own phones
 * are listed in their row under Users & Access.
 */
export default function AuthenticatorSettings() {
  const [msg, msgCtx] = message.useMessage();

  if (!isAdminUser()) {
    return (
      <Note tone="info" icon={<CheckCircle2 size={15} />}>
        Authenticator settings can only be viewed and changed by an administrator.
      </Note>
    );
  }

  return (
    <>
      {msgCtx}
      <OnboardingSection />
      <DeviceSettingsSection notify={msg} />
      <PendingSection notify={msg} />
      <CampaignSection notify={msg} />
    </>
  );
}
